import { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Field, Section } from '@/shared/PanelPrimitives/PanelPrimitives';
import { Modal, useToast } from '@/ui';
import { InfoIcon } from '@/ui/icons';
import { usePricing } from '@/hooks';
import {
  apiErrorName,
  createPayment,
  getBillingProfiles,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { firstBillingProfile, formatCLP } from '@/lib';
import EditarFacturacionModal from '@/pages/facturacion/EditarFacturacionModal.jsx';

import './CheckoutModal.css';

// CheckoutModal — the shared checkout of one offer ("Pasarela de pago" step).
// A @/ui/Modal that replaces the retired CheckoutDrawer (RightSidebar shell):
// shows the offer, the REAL charge breakdown (honorarios + Recepthor service
// fee of the current tier + IVA, via GET /pricing/calculate), the billing
// profile, and the exclusivity alert. "Pagar" POSTs /payments/payin and
// redirects to Toku (window.location: once the link arrives we leave the app).
//
// Single entry point: the detail (/diligencias/:id). /pagos no longer opens
// the checkout in-place — the cola navigates to the detail (single-request
// flow); the one exception is the experimental bulk pay («Pagar N»), which
// queues this modal one diligencia at a time. `children` renders BEFORE the
// sections — that flow uses it to mount the competing-offers list
// (seal-check selection).

// Los 409 del inicio de pago se traducen acá y no en el resto de los 4xx. El
// endpoint documenta tres (LivePaymentAttemptExists, StageTransitionRejected y
// OfferChangedDuringCheckout), y su mensaje del catálogo llega en inglés:
// - OfferChangedDuringCheckout: reintentar repetiría el 409, o sin expectedPrice
//   cobraría el precio nuevo sin que el pagador lo vea; se relee la cotización
//   y vuelve a apretar Pagar.
// - LivePaymentAttemptExists: otro inicio ya tomó el carril; es transitorio y
//   el toast ofrece reintentar. StageTransitionRejected comparte hoy ese mensaje.
//
// El resto de los 4xx conserva el mensaje del API. Traducir el catálogo entero
// es otro alcance, y abarca todos los módulos y no dos mensajes.
const OFFER_CHANGED = 'OfferChangedDuringCheckout';

// The price as the API served it ("60000", "60000.50"). Sent as expectedPrice
// so a correction made before Pagar also answers 409. Anything else is left
// out and the API charges as before.
const PRICE_STRING = /^\d{1,10}(\.\d{1,2})?$/;
function expectedPriceOf(quote) {
  const raw = quote?.expectedPrice;
  return typeof raw === 'string' && PRICE_STRING.test(raw) ? raw : null;
}

function conflictMessage(err) {
  if (err?.status !== 409) return null;
  if (apiErrorName(err?.body) === OFFER_CHANGED)
    return 'El precio de la oferta cambió. Revisa el nuevo monto antes de pagar.';
  return 'Ya hay un cobro en curso para esta oferta. Espera unos segundos y reintenta.';
}

export default function CheckoutModal({
  open,
  onClose,
  kicker = 'Pasarela de pago',
  code,
  title,
  quote,
  siblings = 0,
  children,
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [paying, setPaying] = useState(false);
  // Pagar stays off after OfferChangedDuringCheckout until the refetch lands,
  // so the old amount is never payable next to an enabled button.
  const [refreshing, setRefreshing] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const price = Number(quote?.price) || 0;

  // The org's billing profile — fetched only while the modal is open.
  const {
    data: billingProfilesData,
    isFetched: billingFetched,
    // isPending, not isLoading: a query paused offline is pending without
    // fetching, and still has no answer about the profile.
    isPending: billingPending,
  } = useQuery({
    queryKey: qk.billingProfiles.list(),
    queryFn: () => getBillingProfiles().catch(() => null),
    enabled: open && Boolean(quote),
  });
  // Without a quote the query is disabled and stays pending: nothing loads.
  const billingLoading = Boolean(quote) && billingPending;
  const billingProfile = firstBillingProfile(billingProfilesData);
  // A missing profile disables Pagar and offers to complete it in place, with
  // EditarFacturacionModal.
  const missingBilling = billingFetched && !billingProfile;

  // Real charge breakdown. GET /pricing/calculate returns the service FEE of
  // the next notification — `{ notificationNumber, tier, price: { netAmount,
  // ivaAmount, totalAmount } }` — independent of the offer amount (it takes no
  // params). The payin's total charge is honorarios + fee (IVA applies ONLY to
  // the Recepthor service; the receptor invoices their honorarios themselves).
  // Display-only — on pay, the backend recomputes and reserves the number
  // atomically. Shared usePricing hook: same org-scoped cache entry as the
  // pay panels — no duplicated inline query with a divergent key.
  // Si el fee no se pudo leer, el total no se puede componer. Antes eso rendía
  // un guion, que es lo mismo que rinde el fee todavía cargando, así que el
  // pagador esperaba un número que no iba a llegar.
  const { data: pricingData, isError: pricingFailed } = usePricing({
    enabled: open && Boolean(quote),
  });
  const calc = pricingData?.data ?? pricingData ?? null;
  const fee = calc?.price ?? null;
  // Without the fee yet (loading / error) we do NOT invent a total: show "—";
  // the definitive amount is emitted by the backend on the payin.
  const total = fee != null ? price + (Number(fee.totalAmount) || 0) : null;

  async function handlePay() {
    if (!quote || paying || refreshing) return;
    setPaying(true);
    const toastId = toast({
      kind: 'loading',
      message: 'Redirigiendo al pago…',
      dismissLabel: 'en curso',
    });
    try {
      let profile = billingProfile;
      if (!profile) {
        const profiles = await getBillingProfiles().catch(() => null);
        profile = firstBillingProfile(profiles);
      }
      const profileId = profile?.billingProfileId ?? profile?.id;
      if (!profileId) {
        throw new Error('No hay un perfil de facturación configurado.');
      }
      const expectedPrice = expectedPriceOf(quote);
      const res = await createPayment({
        billingProfileId: profileId,
        quoteId: quote.id,
        ...(expectedPrice ? { expectedPrice } : {}),
      });
      if (res?.linkPayment) {
        window.location.assign(res.linkPayment);
        return;
      }
      throw new Error('No se recibió el enlace de pago.');
    } catch (err) {
      console.error('createPayment failed', err);
      const offerChanged =
        err?.status === 409 && apiErrorName(err?.body) === OFFER_CHANGED;
      if (offerChanged) setRefreshing(true);
      toast.update(toastId, {
        kind: 'err',
        message:
          conflictMessage(err) || err?.message || 'No se pudo iniciar el pago.',
        duration: 0,
        ...(offerChanged
          ? {}
          : { actionLabel: 'Reintentar', action: handlePay }),
      });
      setPaying(false);
      if (offerChanged) {
        // The diligencia reads the price from quotes, the bulk flow from the
        // cola. invalidateQueries resolves once the refetch settles and does
        // not reject: if it failed, the quote keeps the old expectedPrice and
        // the API answers the same 409 without charging.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: qk.quotes.all() }),
          queryClient.invalidateQueries({ queryKey: qk.pagos.all() }),
        ]);
        setRefreshing(false);
      }
    }
  }

  const guardedClose = () => {
    if (!paying) onClose?.();
  };

  // The billing form replaces the checkout's own Modal while it is open, so
  // the quote stays in this component. On save it invalidates the billing
  // profiles, as Facturacion.jsx does, and the checkout re-reads them.
  if (showBillingModal) {
    return (
      <EditarFacturacionModal
        onClose={() => setShowBillingModal(false)}
        onSaved={async () => {
          await queryClient.invalidateQueries({
            queryKey: qk.billingProfiles.all(),
          });
          setShowBillingModal(false);
        }}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={guardedClose}
      size="small"
      closeOnBackdrop={!paying}
    >
      <Modal.Header
        kicker={kicker}
        title="Confirmar pago"
        onClose={guardedClose}
      />
      <Modal.Body>
        {(code || title) && (
          <div className="cod-ctx">
            {code && <span className="cod-ctx__id">{code}</span>}
            {title && <span className="cod-ctx__t">{title}</span>}
          </div>
        )}
        {children}
        {quote && (
          <>
            <Section label="Oferta del receptor">
              <Field label="Receptor" value={quote.name} />
              <Field label="Honorarios" value={formatCLP(quote.price)} mono />
              <Field label="Fecha ofrecida" value={quote.fecha || '—'} />
            </Section>
            <Section label="Cobro">
              <div className="cod-totals">
                <div className="cod-row">
                  <span>Honorarios del receptor</span>
                  <span>{formatCLP(quote.price)}</span>
                </div>
                <div className="cod-row">
                  <span>
                    Servicio Recepthor{calc?.tier ? ` · tier ${calc.tier}` : ''}
                  </span>
                  <span>{fee ? formatCLP(fee.netAmount) : '—'}</span>
                </div>
                <div className="cod-row">
                  <span>IVA · 19% (servicio)</span>
                  <span>{fee ? formatCLP(fee.ivaAmount) : '—'}</span>
                </div>
                <div className="cod-row cod-row--grand">
                  <span>Total a cobrar</span>
                  <span>
                    {total != null
                      ? formatCLP(total)
                      : pricingFailed
                        ? 'No se pudo calcular'
                        : '—'}
                  </span>
                </div>
              </div>
            </Section>
            <Section label="Facturación">
              {billingLoading ? (
                <div className="cod-nobill" role="status">
                  Cargando el perfil de facturación…
                </div>
              ) : billingProfile ? (
                // The FULL profile the invoice will be issued with — the lawyer
                // confirms every tax field before paying.
                <>
                  <Field
                    label="Razón social"
                    value={
                      billingProfile.legalName ||
                      billingProfile.businessName ||
                      billingProfile.razonSocial ||
                      billingProfile.name ||
                      '—'
                    }
                  />
                  <Field label="RUT" value={billingProfile.rut || '—'} mono />
                  <Field
                    label="Giro"
                    value={billingProfile.businessActivity || '—'}
                  />
                  <Field
                    label="ACTECO"
                    value={billingProfile.actecoCode || '—'}
                    mono
                  />
                  <Field
                    label="Dirección"
                    value={
                      [
                        billingProfile.address,
                        billingProfile.commune,
                        billingProfile.city,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'
                    }
                  />
                  <Field label="Email" value={billingProfile.email || '—'} />
                  <Field
                    label="Teléfono"
                    value={billingProfile.phone || '—'}
                    mono
                  />
                </>
              ) : missingBilling ? (
                <div className="cod-nobill">
                  No hay un perfil de facturación configurado en la
                  organización.
                  {missingBilling && (
                    <button
                      type="button"
                      className="cod-link"
                      onClick={() => setShowBillingModal(true)}
                    >
                      Completar datos de facturación →
                    </button>
                  )}
                </div>
              ) : null}
            </Section>
            <div className="cod-alert" role="status">
              <InfoIcon />
              <div>
                Al pagar, esta oferta queda <b>adjudicada</b>
                {siblings > 0
                  ? ` y ${siblings === 1 ? 'la otra se cierra' : `las otras ${siblings} se cierran`}`
                  : ' y el resto se cierra'}
                . La diligencia pasa a <b>Procesando pago</b>.
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Foot>
        <button
          type="button"
          className="cod-btn cod-btn--ghost"
          onClick={() => onClose?.()}
          disabled={paying}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="cod-btn cod-btn--seal"
          onClick={handlePay}
          disabled={
            paying || refreshing || billingLoading || !billingProfile || !quote
          }
          title={
            billingLoading
              ? 'Cargando el perfil de facturación…'
              : missingBilling
                ? 'No hay un perfil de facturación configurado.'
                : undefined
          }
        >
          {paying
            ? 'Redirigiendo…'
            : total != null
              ? `Pagar ${formatCLP(total)} →`
              : 'Pagar →'}
        </button>
      </Modal.Foot>
    </Modal>
  );
}

CheckoutModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  kicker: PropTypes.string,
  code: PropTypes.string,
  title: PropTypes.string,
  quote: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expectedPrice: PropTypes.string,
    fecha: PropTypes.string,
  }),
  siblings: PropTypes.number,
  children: PropTypes.node,
};
